import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  createSession as createSupabaseSession,
  setTopic as setSupabaseTopic,
  setIssue as setSupabaseIssue,
  revealVotes as revealSupabaseVotes,
  resetVotes as resetSupabaseVotes,
  getSessionData,
  subscribeToSession,
  unsubscribeFromSession,
  kickPlayer as kickSupabasePlayer
} from '../lib/planningPoker'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SkipNavigation from '../components/SkipNavigation'
import IssueSearch from '../components/IssueSearch'
import { exportToCSV, exportToJSON, exportToPDF } from '../lib/exportUtils'

function Moderator() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [sessionCode, setSessionCode] = useState(null)
  const [sessionUrl, setSessionUrl] = useState('')
  const [topic, setTopic] = useState('')
  const [currentTopic, setCurrentTopic] = useState('')
  const [issueUrl, setIssueUrl] = useState('')
  const [issueTitle, setIssueTitle] = useState('')
  const [currentIssue, setCurrentIssue] = useState(null)
  const [issueError, setIssueError] = useState('')
  const [players, setPlayers] = useState([])
  const [voteCount, setVoteCount] = useState(0)
  const [totalPlayers, setTotalPlayers] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [allVoted, setAllVoted] = useState(false)
  const [rounds, setRounds] = useState([])
  const [channel, setChannel] = useState(null)
  const [liveAnnouncement, setLiveAnnouncement] = useState('')
  const [copyFeedback, setCopyFeedback] = useState('')
  const [openMenuPlayerId, setOpenMenuPlayerId] = useState(null)

  useEffect(() => {
    // Subscribe to session updates
    if (sessionCode) {
      const newChannel = subscribeToSession(sessionCode, async (update) => {
        // Fetch fresh session data whenever there's a change
        const sessionData = await getSessionData(sessionCode, true)
        if (sessionData) {
          updateSessionData(sessionData)
        }
      })

      setChannel(newChannel)

      // Initial data fetch
      getSessionData(sessionCode, true).then((sessionData) => {
        if (sessionData) {
          updateSessionData(sessionData)
        }
      })

      return () => {
        unsubscribeFromSession(newChannel)
      }
    }
  }, [sessionCode])

  const updateSessionData = (data) => {
    const playersList = data.players || []
    const previousVoteCount = voteCount
    const previousPlayerCount = totalPlayers
    const wasRevealed = revealed

    setPlayers(playersList)

    const voted = playersList.filter((p) => p.vote !== null && p.vote !== undefined).length
    setVoteCount(voted)
    setTotalPlayers(playersList.length)
    setAllVoted(voted === playersList.length && playersList.length > 0)

    // Announce changes to screen readers
    if (playersList.length > previousPlayerCount) {
      setLiveAnnouncement(t('accessibility.playerJoinedModerator', { count: playersList.length, defaultValue: `Player joined. Total: ${playersList.length}` }))
    }

    if (voted > previousVoteCount && voted === playersList.length && playersList.length > 0) {
      setLiveAnnouncement(t('accessibility.allPlayersVoted', 'All players have voted'))
    } else if (voted !== previousVoteCount) {
      setLiveAnnouncement(t('accessibility.voteCountUpdate', { voted, total: playersList.length, defaultValue: `${voted} of ${playersList.length} players voted` }))
    }

    if (data.revealed && !wasRevealed) {
      setLiveAnnouncement(t('accessibility.votesRevealedModerator', 'Votes have been revealed'))
    } else if (!data.revealed && wasRevealed) {
      setLiveAnnouncement(t('accessibility.newRoundStarted', 'New round started'))
    }

    setRevealed(data.revealed || false)
    setRounds(data.rounds || [])
    setCurrentTopic(data.topic || '')
    setCurrentIssue(data.issue || null)
  }

  const createSession = async () => {
    const response = await createSupabaseSession()
    if (response.success) {
      setSessionCode(response.code)
      setSessionUrl(response.url)
    } else {
      alert(t('moderator.errorCreating'))
    }
  }

  const handleSetTopic = async () => {
    if (topic.trim() && sessionCode) {
      await setSupabaseTopic(sessionCode, topic.trim())
      setTopic('')
    }
  }

  const handleSetIssue = async () => {
    if (sessionCode) {
      setIssueError('')
      const result = await setSupabaseIssue(sessionCode, issueUrl.trim(), issueTitle.trim() || null)

      if (result.success) {
        setIssueUrl('')
        setIssueTitle('')
      } else {
        setIssueError(result.error || t('moderator.issueError'))
      }
    }
  }

  const handleClearIssue = async () => {
    if (sessionCode) {
      setIssueError('')
      await setSupabaseIssue(sessionCode, '', null)
      setIssueUrl('')
      setIssueTitle('')
    }
  }

  const handleIssueSelect = async (issue) => {
    // Set the issue fields from the selected issue
    setIssueUrl(issue.url)
    setIssueTitle(issue.title)
    setIssueError('')

    // If topic is empty, set it to the issue title
    if (sessionCode && !currentTopic) {
      await setSupabaseTopic(sessionCode, issue.title)
    }

    // Automatically submit the selected issue
    if (sessionCode) {
      const result = await setSupabaseIssue(sessionCode, issue.url, issue.title)

      if (!result.success) {
        setIssueError(result.error || t('moderator.issueError'))
      }
    }
  }

  const handleReveal = async () => {
    await revealSupabaseVotes(sessionCode)
  }

  const handleReset = async () => {
    await resetSupabaseVotes(sessionCode)
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
    setCopyFeedback(t('accessibility.copiedToClipboard', { item: label, defaultValue: `${label} copied to clipboard` }))
    setTimeout(() => setCopyFeedback(''), 3000)
  }

  const handleExportCSV = () => {
    exportToCSV(rounds, sessionCode, currentTopic, currentIssue, players, revealed)
  }

  const handleExportJSON = () => {
    exportToJSON(rounds, sessionCode, currentTopic, currentIssue, players, revealed)
  }

  const handleExportPDF = () => {
    exportToPDF(rounds, sessionCode, currentTopic, currentIssue, players, revealed)
  }

  const handleKickPlayer = async (playerClientId, playerName) => {
    if (!confirm(t('moderator.kickConfirm', { name: playerName, defaultValue: `Remove ${playerName} from the session?` }))) {
      return
    }

    setOpenMenuPlayerId(null)
    const result = await kickSupabasePlayer(sessionCode, playerClientId)

    if (!result.success) {
      alert(t('moderator.kickError', { defaultValue: 'Failed to remove player' }))
    }
  }

  const getVoteStats = () => {
    const numericVotes = players
      .filter(p => p.vote !== null && !isNaN(parseFloat(p.vote)))
      .map(p => parseFloat(p.vote))

    if (numericVotes.length === 0) return null

    const avg = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length
    const sorted = [...numericVotes].sort((a, b) => a - b)
    const median = sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)]
    const min = Math.min(...numericVotes)
    const max = Math.max(...numericVotes)

    return { avg, median, min, max }
  }

  const getVoteGroups = () => {
    const groups = {}
    players.forEach(player => {
      if (player.vote !== null) {
        if (!groups[player.vote]) {
          groups[player.vote] = []
        }
        groups[player.vote].push(player.name)
      }
    })
    return Object.entries(groups).sort((a, b) => {
      const aNum = parseFloat(a[0])
      const bNum = parseFloat(b[0])
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
      return a[0].localeCompare(b[0])
    })
  }

  const stats = revealed ? getVoteStats() : null
  const voteGroups = revealed ? getVoteGroups() : []

  if (!sessionCode) {
    return (
      <>
        <SkipNavigation />
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2" role="navigation" aria-label={t('accessibility.utilities', 'Utilities')}>
          <DarkModeToggle />
          <LanguageSwitcher />
        </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 max-w-md w-full transition-colors" id="main-content">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2 dark:text-white">
                <span aria-hidden="true">👑 </span>
                {t('moderator.title')}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">{t('moderator.createNewSession')}</p>
            </div>
            <button
              onClick={createSession}
              className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-xl p-4 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg"
              aria-label={t('moderator.startSession')}
            >
              {t('moderator.startSession')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl p-3 transition-all font-semibold"
              aria-label={t('common.back')}
            >
              {t('common.back')}
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SkipNavigation />
      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      {/* Copy feedback announcement */}
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {copyFeedback}
      </div>
      <div className="min-h-screen p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-white">
              <span aria-hidden="true">👑 </span>
              {t('moderator.title')}
            </h1>
            <div className="flex items-center gap-3" role="navigation" aria-label={t('accessibility.utilities', 'Utilities')}>
            <DarkModeToggle />
            <LanguageSwitcher />
            <button
              onClick={() => navigate('/')}
              className="bg-white/20 hover:bg-white/30 dark:bg-gray-800/50 dark:hover:bg-gray-800/70 text-white px-4 py-2 rounded-lg transition-all"
            >
              {t('common.back')}
            </button>
          </div>
        </div>

        {/* Session Info */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700 rounded-2xl shadow-2xl p-6 mb-6 transition-colors" id="main-content">
          <h2 className="text-white text-xl font-bold mb-4">{t('moderator.sessionCode')}</h2>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-5xl font-bold text-white tracking-widest font-mono">
              {sessionCode}
            </span>
            <button
              onClick={() => copyToClipboard(sessionCode, t('moderator.sessionCode'))}
              className="bg-white/20 hover:bg-white/30 dark:bg-gray-900/30 dark:hover:bg-gray-900/50 text-white px-4 py-2 rounded-lg transition-all"
              aria-label={t('accessibility.copySessionCode', 'Copy session code')}
            >
              {t('common.copy')}
            </button>
          </div>
          <p className="text-white/90 mb-3">{t('moderator.playersCanJoin')}</p>
          <div className="flex gap-2 mb-3">
            <label htmlFor="session-url" className="sr-only">{t('moderator.sessionUrl', 'Session URL')}</label>
            <input
              id="session-url"
              type="text"
              value={sessionUrl}
              readOnly
              className="flex-1 px-4 py-2 rounded-lg bg-white/90 dark:bg-gray-900/30 dark:text-white dark:placeholder-gray-400"
              aria-label={t('accessibility.sessionUrlField', 'Session URL')}
            />
            <button
              onClick={() => copyToClipboard(sessionUrl, t('moderator.sessionUrl', 'Session URL'))}
              className="bg-white/20 hover:bg-white/30 dark:bg-gray-900/30 dark:hover:bg-gray-900/50 text-white px-4 py-2 rounded-lg transition-all whitespace-nowrap"
              aria-label={t('accessibility.copySessionUrl', 'Copy session URL')}
            >
              {t('moderator.copyUrl')}
            </button>
          </div>
          <button
            onClick={() => window.open(`${sessionUrl}&name=Moderator`, '_blank')}
            className="w-full bg-white/20 hover:bg-white/30 dark:bg-gray-900/30 dark:hover:bg-gray-900/50 text-white px-4 py-3 rounded-lg transition-all font-semibold flex items-center justify-center gap-2"
            aria-label={t('moderator.openPlayerView')}
          >
            <span aria-hidden="true">🎮</span>
            <span>{t('moderator.openPlayerView')}</span>
          </button>
        </div>

        {/* Topic & Issue */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 transition-colors">
          <h3 className="text-xl font-bold mb-4 dark:text-white">{t('moderator.currentTopic')}</h3>
          <div className="flex gap-3 mb-4">
            <div className="flex-1">
              <label htmlFor="topic-input" className="sr-only">{t('moderator.topicPlaceholder')}</label>
              <input
                id="topic-input"
                type="text"
                placeholder={t('moderator.topicPlaceholder')}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSetTopic()}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 transition-colors"
              />
            </div>
            <button
              onClick={handleSetTopic}
              className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
              aria-label={t('moderator.setTopic')}
            >
              {t('moderator.setTopic')}
            </button>
          </div>
          {currentTopic && (
            <div className="bg-indigo-50 dark:bg-indigo-900/30 border-l-4 border-indigo-500 dark:border-indigo-400 p-4 rounded mb-4">
              <p className="font-semibold text-indigo-900 dark:text-indigo-100">{currentTopic}</p>
            </div>
          )}

          {/* Issue Integration */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-semibold mb-3 dark:text-white flex items-center gap-2">
              <span>🔗</span>
              <span>{t('moderator.linkedIssue')}</span>
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                ({t('moderator.optional')})
              </span>
            </h4>

            {currentIssue ? (
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-4 rounded mb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {currentIssue.platform === 'github' ? 'GitHub' : 'GitLab'}
                      </span>
                      {currentIssue.number && (
                        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                          {currentIssue.number}
                        </span>
                      )}
                    </div>
                    {currentIssue.title && (
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {currentIssue.title}
                      </p>
                    )}
                    <a
                      href={currentIssue.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-700 dark:text-green-400 hover:underline break-all"
                    >
                      {currentIssue.url}
                    </a>
                  </div>
                  <button
                    onClick={handleClearIssue}
                    className="bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 px-3 py-1 rounded text-sm font-semibold transition-all"
                  >
                    {t('common.remove')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Issue Search - Only shown when logged in with GitHub/GitLab */}
                <IssueSearch onIssueSelect={handleIssueSelect} />

                {/* Divider with "OR" text */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      {t('common.or', 'or')}
                    </span>
                  </div>
                </div>

                {/* Manual Issue Input */}
                <div className="flex gap-3 mb-3">
                  <input
                    type="url"
                    placeholder={t('moderator.issueUrlPlaceholder')}
                    value={issueUrl}
                    onChange={(e) => {
                      setIssueUrl(e.target.value)
                      setIssueError('')
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSetIssue()}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 dark:focus:border-green-400 focus:outline-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 transition-colors"
                  />
                </div>
                <div className="flex gap-3 mb-3">
                  <input
                    type="text"
                    placeholder={t('moderator.issueTitlePlaceholder')}
                    value={issueTitle}
                    onChange={(e) => setIssueTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSetIssue()}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-green-500 dark:focus:border-green-400 focus:outline-none bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 transition-colors"
                  />
                  <button
                    onClick={handleSetIssue}
                    disabled={!issueUrl.trim()}
                    className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
                  >
                    {t('moderator.linkIssue')}
                  </button>
                </div>
                {issueError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-400 p-3 rounded">
                    <p className="text-sm text-red-700 dark:text-red-400">{issueError}</p>
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('moderator.issueHint')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Players Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6 transition-colors">
          <h3 className="text-xl font-bold mb-4 dark:text-white">
            {t('common.players')} ({totalPlayers})
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
            {players.map((player, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border-2 transition-all relative ${
                  player.vote !== null
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold dark:text-white flex-1">{player.name}</div>
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuPlayerId(openMenuPlayerId === player.id ? null : player.id)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      aria-label={t('moderator.playerMenu', { defaultValue: 'Player menu' })}
                    >
                      ⋮
                    </button>
                    {openMenuPlayerId === player.id && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setOpenMenuPlayerId(null)}
                        />
                        <div className="absolute right-0 mt-1 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-20 min-w-[120px]">
                          <button
                            onClick={() => handleKickPlayer(player.id, player.name)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            {t('moderator.kickPlayer', { defaultValue: 'Remove' })}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-2xl">
                  {revealed ? (
                    <span className="text-indigo-600 dark:text-indigo-400 font-bold">
                      {player.vote !== null ? player.vote : '—'}
                    </span>
                  ) : (
                    <span className="dark:text-gray-300">{player.vote !== null ? '✓' : '⏳'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div
            className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-6 transition-colors"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <p className="text-center font-semibold dark:text-white">
              {t('moderator.votedCount', { count: voteCount, total: totalPlayers })}
            </p>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={handleReveal}
              disabled={voteCount === 0 || revealed}
              className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
              aria-label={t('moderator.revealCards')}
              aria-disabled={voteCount === 0 || revealed}
            >
              <span aria-hidden="true">🃏 </span>
              {t('moderator.revealCards')}
            </button>
            <button
              onClick={handleReset}
              disabled={!revealed}
              className="bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
              aria-label={t('moderator.newRound')}
              aria-disabled={!revealed}
            >
              <span aria-hidden="true">🔄 </span>
              {t('moderator.newRound')}
            </button>
          </div>
        </div>

        {/* Results */}
        {revealed && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 transition-colors" role="region" aria-label={t('accessibility.votingResults', 'Voting results')}>
            <h2 className="text-xl font-bold mb-4 dark:text-white">{t('moderator.results')}</h2>

            {/* Vote Groups */}
            <div className="space-y-3 mb-6">
              {voteGroups.map(([vote, names]) => (
                <div key={vote} className="bg-gray-50 dark:bg-gray-700 border-l-4 border-indigo-500 dark:border-indigo-400 p-4 rounded transition-colors">
                  <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">{vote}</div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {names.map((name, idx) => (
                      <span key={idx} className="bg-white dark:bg-gray-600 px-3 py-1 rounded-full text-sm border border-gray-300 dark:border-gray-500 dark:text-gray-200">
                        {name}
                      </span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {names.length} {names.length === 1 ? t('moderator.votes') : t('moderator.votes_plural')}
                  </div>
                </div>
              ))}
            </div>

            {/* Statistics */}
            {stats && (
              <>
                <h3 className="font-bold mb-3 dark:text-white">{t('moderator.statistics')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg text-center transition-colors">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('moderator.average')}</div>
                    <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{stats.avg.toFixed(1)}</div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg text-center transition-colors">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('moderator.median')}</div>
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.median}</div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg text-center transition-colors">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('moderator.min')}</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.min}</div>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg text-center transition-colors">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('moderator.max')}</div>
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.max}</div>
                  </div>
                </div>
              </>
            )}

            {/* Export Buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="font-bold mb-3 dark:text-white">{t('moderator.exportResults', 'Export Results')}</h3>
              <div className="flex flex-wrap gap-3" role="group" aria-label={t('accessibility.exportOptions', 'Export options')}>
                <button
                  onClick={handleExportCSV}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
                  aria-label={t('moderator.exportCSV', 'Export CSV')}
                >
                  <span aria-hidden="true">📊</span>
                  <span>{t('moderator.exportCSV', 'Export CSV')}</span>
                </button>
                <button
                  onClick={handleExportJSON}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
                  aria-label={t('moderator.exportJSON', 'Export JSON')}
                >
                  <span aria-hidden="true">📄</span>
                  <span>{t('moderator.exportJSON', 'Export JSON')}</span>
                </button>
                <button
                  onClick={handleExportPDF}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2"
                  aria-label={t('moderator.exportPDF', 'Export PDF')}
                >
                  <span aria-hidden="true">📑</span>
                  <span>{t('moderator.exportPDF', 'Export PDF')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Round History */}
        {rounds.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mt-6 transition-colors" role="region" aria-label={t('accessibility.roundHistory', 'Round history')}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                <span aria-hidden="true">📋 </span>
                {t('moderator.roundHistory')}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleExportCSV}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                  title={t('moderator.exportCSV', 'Export CSV')}
                >
                  📊 CSV
                </button>
                <button
                  onClick={handleExportJSON}
                  className="bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                  title={t('moderator.exportJSON', 'Export JSON')}
                >
                  📄 JSON
                </button>
                <button
                  onClick={handleExportPDF}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                  title={t('moderator.exportPDF', 'Export PDF')}
                >
                  📑 PDF
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {rounds.map((round, idx) => (
                <div key={round.id || idx} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-gray-200 dark:border-gray-600 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-bold text-lg dark:text-white">
                        {t('moderator.round')} {rounds.length - idx}
                      </h4>
                      <p className="text-indigo-600 dark:text-indigo-400 font-semibold mt-1">{round.topic}</p>
                      {round.issue_url && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                            {round.issue_platform === 'github' ? 'GitHub' : 'GitLab'}
                          </span>
                          {round.issue_number && (
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400">
                              {round.issue_number}
                            </span>
                          )}
                          {round.issue_title && (
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {round.issue_title}
                            </span>
                          )}
                          <a
                            href={round.issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            🔗
                          </a>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {new Date(round.completed_at).toLocaleString()}
                      </p>
                    </div>
                    {round.average !== null && (
                      <div className="flex gap-2 text-sm">
                        <div className="bg-indigo-100 dark:bg-indigo-900/30 px-3 py-1 rounded">
                          <span className="text-gray-600 dark:text-gray-400">Ø </span>
                          <span className="font-bold text-indigo-600 dark:text-indigo-400">{Number(round.average).toFixed(1)}</span>
                        </div>
                        <div className="bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded">
                          <span className="text-gray-600 dark:text-gray-400">⌀ </span>
                          <span className="font-bold text-purple-600 dark:text-purple-400">{round.median}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {round.votes
                      .filter(v => v.vote !== null)
                      .sort((a, b) => {
                        const aNum = parseFloat(a.vote);
                        const bNum = parseFloat(b.vote);
                        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                        return a.vote.localeCompare(b.vote);
                      })
                      .map((vote, vIdx) => (
                        <div key={vIdx} className="bg-white dark:bg-gray-600 px-3 py-1 rounded border border-gray-300 dark:border-gray-500 transition-colors">
                          <span className="font-semibold text-indigo-600 dark:text-indigo-400 mr-2">{vote.vote}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{vote.name}</span>
                        </div>
                      ))}
                  </div>

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('moderator.participants')}: {round.votes.filter(v => v.vote !== null).length}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {rounds.length === 0 && sessionCode && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mt-6 text-center transition-colors">
            <p className="text-gray-500 dark:text-gray-400 italic">{t('moderator.noRoundsYet')}</p>
          </div>
        )}
        </div>
      </div>
    </>
  )
}

export default Moderator
